import React, {
  createContext,
  useContext,
  useMemo,
  useState,
  useEffect,
  useCallback
} from "react";
import { useChecklistDefaults } from "./ChecklistDefaultsContext";
import { useWeather } from "./WeatherContext";

const HazardSelectionContext = createContext(null);

const normalizeHazardId = (value) => {
  if (!value || typeof value !== "string") return null;
  return value.trim().toLowerCase().replace(/_/g, "-").replace(/\s+/g, "-");
};

const WEATHER_TO_HAZARD_ID = {
  Routine: "routine",
  Flooding: "flooding",
  Hurricane: "hurricane",
  "Severe Storms": "severe-storms",
  Tornado: "tornado",
  Wildfire: "wildfire",
  "Winter Weather": "winter-weather",
  "Extreme Heat": "extreme_heat",
  "Extreme Cold": "extreme-cold",
  "High Winds": "high-winds",
  "Dense Fog": "dense-fog",
  "Surf & Rip Currents": "surf-rip",
};

export const HazardSelectionProvider = ({ children }) => {
  const {
    activeHazardIds: planningHazardIds,
    primaryContext,
  } = useChecklistDefaults();

  const { weather, alertType, updateWeather, updateAlertType } = useWeather();

  const [activeMode, setActiveMode] = useState("context");
  const [selectedHazardId, setSelectedHazardId] = useState(null);
  const [selectedAlertType, setSelectedAlertType] = useState(null);

  useEffect(() => {
    if (!weather) return;

    if (weather === "All Hazards" || weather === "all-hazards") {
      setSelectedHazardId(null);
      setSelectedAlertType(null);
      setActiveMode("context");
      return;
    }

    const mapped = WEATHER_TO_HAZARD_ID[weather];
    const normalized = normalizeHazardId(mapped || weather);

    if (!normalized) {
      setSelectedHazardId(null);
      setSelectedAlertType(null);
      setActiveMode("context");
      return;
    }

    setSelectedHazardId(normalized);

    // Only derive from WeatherContext if user hasn't explicitly chosen context mode
    if (activeMode !== "context") {
      setActiveMode("hazard");
    }
  }, [weather, activeMode]); // intentionally only weather-driven

  const effectiveHazardId =
    selectedHazardId ?? primaryContext?.hazardId ?? null;

  const effectiveAlertType =
    activeMode === "hazard"
      ? selectedAlertType ?? alertType ?? null
      : primaryContext?.alertType ?? alertType ?? null;

  const activeHazardIds = useMemo(() => {
    if (activeMode === "context") {
      return planningHazardIds || [];
    }

    if (activeMode === "hazard" && effectiveHazardId) {
      return [normalizeHazardId(effectiveHazardId)];
    }

    return [];
  }, [activeMode, planningHazardIds, effectiveHazardId]);

const selectPlanningContext = useCallback(() => {
  setSelectedHazardId(null);
  setSelectedAlertType(null);
  setActiveMode("context");

  if (weather !== "All Hazards") {
    updateWeather("All Hazards");
  }
}, [weather, updateWeather]);

const selectSpecificHazard = useCallback(
  (hazardId, nextAlertType = null) => {
    if (!hazardId) return;

    const normalized = normalizeHazardId(hazardId);
    if (!normalized) return;

    setSelectedHazardId(normalized);
    setSelectedAlertType(nextAlertType || null);
    setActiveMode("hazard");

    if (nextAlertType && updateAlertType) {
      updateAlertType(nextAlertType);
    }
  },
  [updateAlertType],
);

const value = useMemo(
  () => ({
    activeMode,
    activeHazardId:
      activeMode === "hazard"
        ? normalizeHazardId(effectiveHazardId)
        : null,
    activeHazardIds,
    activeAlertType: effectiveAlertType,
    planningHazardIds: planningHazardIds || [],
    selectPlanningContext,
    selectSpecificHazard,
    setActiveMode,
    setSelectedHazardId,
    setSelectedAlertType,
  }),
  [
    activeMode,
    effectiveHazardId,
    effectiveAlertType,
    activeHazardIds,
    planningHazardIds,
    selectPlanningContext,
    selectSpecificHazard,
  ],
);

  return (
    <HazardSelectionContext.Provider value={value}>
      {children}
    </HazardSelectionContext.Provider>
  );
};

export const useHazardSelection = () => {
  const ctx = useContext(HazardSelectionContext);
  if (!ctx) {
    throw new Error(
      "useHazardSelection must be used within HazardSelectionProvider"
    );
  }
  return ctx;
};